import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import client from "@/api/client";
import i18n from "@/i18n";

const SiteContentContext = createContext(null);

// Strip empty / whitespace-only values so a blanked admin field falls back to
// the static i18n default instead of rendering an empty string.
function pruneEmpty(obj) {
  if (!obj || typeof obj !== "object") return {};
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (typeof v === "string") {
      if (v.trim() !== "") out[k] = v;
    } else if (v !== null && v !== undefined) {
      out[k] = v;
    }
  }
  return out;
}

// Merge admin-edited landing / trust-strip overrides into the running i18next
// resource bundles for both languages, then force listeners to re-render.
function applyContentToI18n(content) {
  if (!content || typeof content !== "object") return;
  const landing = content.landing_overrides || {};
  const trust = content.trust_strip_overrides || {};

  for (const lng of ["en", "ar"]) {
    const bundle = {};
    const landingPatch = pruneEmpty(landing[lng]);
    const trustPatch = pruneEmpty(trust[lng]);
    if (Object.keys(landingPatch).length) bundle.landing = landingPatch;
    if (Object.keys(trustPatch).length) bundle.trustStrip = trustPatch;
    if (Object.keys(bundle).length) {
      // deep=true merges into any existing bundle; overwrite=true replaces keys.
      i18n.addResourceBundle(lng, "translation", bundle, true, true);
    }
  }
  // Force useTranslation hooks to re-render with the newly bridged overrides.
  i18n.emit("languageChanged", i18n.language);
}

export function SiteContentProvider({ children }) {
  const [content, setContent] = useState(null);
  const [navPages, setNavPages] = useState([]); // custom pages flagged show_in_nav
  const [loading, setLoading] = useState(true);

  const refreshNavPages = useCallback(async () => {
    try {
      const res = await client.get("/pages");
      const items = (res.data?.items || []).map((p) => ({
        id: `custom-${p.slug}`,
        slug: p.slug,
        path: `/p/${p.slug}`,
        label_en: p.title_en || p.slug,
        label_ar: p.title_ar || p.title_en || p.slug,
        footer_column: p.footer_column || "company",
        nav_placement: p.nav_placement || (p.show_in_nav ? "both" : "hidden"),
      }));
      setNavPages(items);
    } catch (e) {
      console.error("SiteContentContext.refreshNavPages failed:", e);
      setNavPages([]);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const [contentRes] = await Promise.all([
        client.get("/content"),
        refreshNavPages(),
      ]);
      setContent(contentRes.data);
      applyContentToI18n(contentRes.data);
    } catch (e) {
      // Non-fatal: consumers fall back to i18n defaults. Log for diagnostics.
      console.error("SiteContentContext.refresh failed:", e);
    } finally {
      setLoading(false);
    }
  }, [refreshNavPages]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Allow other views (e.g. AdminLandingSectionsManager) to trigger a full
  // content refresh so bridged i18n overrides are re-applied without a page
  // reload.
  useEffect(() => {
    const handler = () => refresh();
    window.addEventListener("site-content:refresh", handler);
    return () => window.removeEventListener("site-content:refresh", handler);
  }, [refresh]);

  const value = useMemo(
    () => ({ content, loading, navPages, refresh, refreshNavPages }),
    [content, loading, navPages, refresh, refreshNavPages],
  );

  return <SiteContentContext.Provider value={value}>{children}</SiteContentContext.Provider>;
}

export function useSiteContent() {
  const ctx = useContext(SiteContentContext);
  if (!ctx) throw new Error("useSiteContent must be used within SiteContentProvider");
  return ctx;
}
